// API client + types for the Professor Elm chatbot (beta-only).
// The backend endpoints live under /chatbot (see backend-go/internal/routes/chat.go).

import api from '@/api/api';

/** One stored turn: the user's question and the Professor's answer. */
export interface ChatTurn {
  user: string;
  assistant: string;
}

/** One rendered bubble in the chat window. */
export interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  /** Card image the Professor attached, when it chose to. */
  imgURL?: string;
  /** Set when the message is a surfaced error, not an answer. */
  isError?: boolean;
}

/** The backend only feeds the model the last 20 turns. */
export const HISTORY_CONTEXT_LIMIT = 20;

/**
 * Server-stored history, for prefilling the window on load. Fails soft: with
 * cache disabled in production this comes back empty and the client keeps its
 * own copy of the conversation instead.
 */
export const fetchChatHistory = async (): Promise<ChatTurn[]> => {
  try {
    const res = await api.get('/chatbot/history');
    return Array.isArray(res.data?.history) ? res.data.history : [];
  } catch {
    return [];
  }
};

/** Ask the Professor to forget everything. */
export const clearChatHistory = async (): Promise<void> => {
  await api.post('/chatbot/new');
};

/**
 * Run one turn. `history` is the client-side context (the server re-sends it
 * to the model when its own storage is unavailable).
 */
export const sendChatTurn = async (
  prompt: string,
  history: ChatTurn[]
): Promise<{ message: string; imgURL?: string }> => {
  const res = await api.post('/chatbot', {
    prompt,
    history: history.slice(-HISTORY_CONTEXT_LIMIT),
  });
  const data = res.data ?? {};
  return {
    message: typeof data.message === 'string' ? data.message : '',
    imgURL: typeof data.imgURL === 'string' && data.imgURL ? data.imgURL : undefined,
  };
};

/** The backend speaks { error: true, message: "..." } — surface the message. */
export function chatErrorMessage(error: unknown): { message: string; status?: number } {
  const err = error as {
    response?: { status?: number; data?: { error?: unknown; message?: unknown } };
    message?: string;
  };
  const data = err?.response?.data;
  const message =
    typeof data?.message === 'string' && data.message
      ? data.message
      : typeof data?.error === 'string' && data.error
        ? data.error
        : err?.message || 'Something went wrong. Please try again.';
  return { message, status: err?.response?.status };
}
